import { CloudOffOutlined, PublishOutlined } from "@mui/icons-material";
import { LinearProgress, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Section } from "components/disw";
import CommandBar from "components/disw/CommandBar";
import CommandBarButton from "components/disw/CommandBarButton";
import CommandBarContainer from "components/disw/CommandBarContainer";
import { ErrorBox } from "components/error";
import {
  RestrictedSolutionRealm,
  SolutionPermission
} from "features/access-management";
import { useSolutionKey } from "features/solution";
import DataSourceVersionContent from "./DataSourceVersionContent";
import DataSourceVersionInfo from "./DataSourceVersionInfo";
import DetailCard from "./DetailCard";
import { useDataSource } from "./hooks/useDataSources";
import { useDataSourceVersionPublishing } from "./useDataSourceVersionPublishing";
import VersionStatusBadge from "./VersionStatusBadge";

const useStyles = makeStyles(theme => ({
  // Undo the shell's content padding, so the scroll area spans from the breadcrumb bar to the command bar,
  // and fill only the height left beside the shell's footer, so the shell itself does not scroll
  page: {
    // bottom: the shell's gap above its footer
    margin: theme.spacing(-3, -3, -2, -3),
    flex: "1 1 0",
    minHeight: 0
  },
  // CommandBarContainer hides overflow, so the content scrolls itself
  main: {
    height: "100%",
    overflowY: "auto",
    boxSizing: "border-box",
    padding: theme.spacing(3),
    // reaches over CommandBarContainer's gap up to the command bar
    marginRight: -16
  }
}));

interface DataSourceVersionViewProps {
  dataSourceKey: string;
  version: string;
}

/** Subpage of a data source version: metadata, schemata and API tester, with (un)publishing in the command bar. */
export default function DataSourceVersionView({
  dataSourceKey,
  version
}: DataSourceVersionViewProps) {
  const classes = useStyles();
  const solutionKey = useSolutionKey();
  const { data, loading, error, refetch } = useDataSource(
    solutionKey,
    dataSourceKey
  );
  const metadata =
    data?.solutions.solution?.cloudDataSources?.dataSource?.allVersions.find(
      item => item.version === version
    );
  const publishing = useDataSourceVersionPublishing({
    solutionKey,
    dataSourceKey,
    version,
    isLive: metadata?.isLive ?? false,
    onChanged: refetch
  });

  if (loading && !data) return <LinearProgress />;
  if (error) return <ErrorBox apolloError={error} />;
  if (!metadata)
    return (
      <Typography>
        Version {version} of data source {dataSourceKey} was not found.
      </Typography>
    );

  return (
    <div className={classes.page}>
      <CommandBarContainer
        main={
          <div className={classes.main}>
            <DetailCard
              title={`${dataSourceKey} ${version}`}
              badge={<VersionStatusBadge isLive={metadata.isLive} />}
              data-testid="data-source-version"
            >
              <Section name="Version">
                <DataSourceVersionInfo version={metadata} />
                {publishing.busy && <LinearProgress />}
                {publishing.error && (
                  <ErrorBox
                    apolloError={publishing.error}
                    title={`Unable to ${publishing.action.toLowerCase()} version`}
                  />
                )}
              </Section>
              <DataSourceVersionContent
                solutionKey={solutionKey}
                dataSourceKey={dataSourceKey}
                version={version}
                isLive={metadata.isLive}
              />
            </DetailCard>
            {publishing.dialog}
          </div>
        }
        sidebar={
          <CommandBar>
            <RestrictedSolutionRealm
              silent
              requiredPermissions={[SolutionPermission.ChangeCloudDataSources]}
            >
              {({ accessGranted, missingPermissions }) => (
                <CommandBarButton
                  icon={
                    metadata.isLive ? <CloudOffOutlined /> : <PublishOutlined />
                  }
                  label={publishing.action}
                  tooltip={
                    accessGranted
                      ? undefined
                      : `Missing permissions: ${missingPermissions?.join(", ")}`
                  }
                  disabled={!accessGranted || publishing.busy}
                  onClick={publishing.requestChange}
                />
              )}
            </RestrictedSolutionRealm>
          </CommandBar>
        }
      />
    </div>
  );
}
